import { useEffect, useState } from 'react';
import { request } from '../../shared/http.js';

// The one call this site makes. It answers 401 for a visitor with no session,
// which is the common case and not an error worth showing anybody - the button
// simply keeps its default wording.
//
// The session cookie is set on the API's own host, and litbyt.com and
// api.litbyt.com share a registrable domain, so SameSite=Lax still lets the
// browser send it from here. Nothing on this page can read the cookie itself;
// asking the API is the only way to know.
const fetchMe = (signal) => request('/api/auth/me', { signal }, {}, 'include');

// How long the button is allowed to stay undecided.
//
// The shared client retries a failed GET, which is right for a display that
// must eventually get its books and wrong here: this is one word on a button,
// and a visitor watching it stay non-committal for eight seconds while the
// retries play out is worse served than one who sees "get started" straight
// away. After this we stop asking and assume nobody is signed in - the button
// still goes to the right place either way.
const DECIDE_WITHIN_MS = 2500;

/**
 * Whether the visitor already has an account open somewhere.
 *
 * Deliberately three states rather than a boolean. Rendering "get started" and
 * then swapping it to "open your bookshelf" a moment later is a button that
 * moves under a cursor, so the caller waits out `null` before committing to
 * either wording.
 *
 * Nothing here blocks the page: the whole site renders while this is in
 * flight, and a visitor who never signs in never notices it happened.
 */
export function useSignedIn() {
  const [signedIn, setSignedIn] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), DECIDE_WITHIN_MS);
    let settled = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(deadline);
      setSignedIn(value);
    };

    fetchMe(controller.signal)
      .then(() => settle(true))
      // 401, a network failure, the API being down, our own deadline - all
      // land on the same side. A marketing page that cannot reach the API
      // should still invite people in.
      .catch(() => settle(false));

    return () => {
      clearTimeout(deadline);
      settled = true;
      controller.abort();
    };
  }, []);

  return signedIn;
}
